<template>
  <v-row justify="center">
    <v-dialog v-model="open" width="560px" scrollable>
      <v-card class="pl-4">
        <!-- close -->
        <v-toolbar elevation="0" class="title">
          <span>{{ $t('message.pages.profile.title') }}</span>
          <v-spacer />
          <v-toolbar-items>
            <v-btn icon @click="handleClose">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <!-- steps -->
        <v-card-text class="pl-8 pr-16 pb-6" style="height: 500px">
          <v-row no-gutters>
            <v-col cols="12">
              <span class="fieldsText">{{
                $t('message.pages.profile.name')
              }}</span>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="user.name"
                outlined
                dense
                max-width="256px"
                style="max-width: 260px"
              ></v-text-field>
            </v-col>
            <v-col v-if="isBrazil" cols="12">
              <span class="fieldsText">{{
                $t('message.pages.profile.document')
              }}</span>
            </v-col>
            <v-col v-if="isBrazil" cols="12">
              <v-text-field
                v-model="user.document"
                v-mask="documentMask"
                outlined
                dense
                required
                :messages="documentErrorMessage"
                :error="errors.invalidCnpj || errors.invalidCpf"
                style="max-width: 260px"
                maxlength="19"
              >
                <template v-slot:message="{ message }">
                  <span
                    class="red--text caption align-text"
                    v-if="errors.invalidCnpj || errors.invalidCpf"
                  >
                    <v-icon color="red" size="18" class="pb-1">
                      mdi-alert-circle</v-icon
                    >
                    {{ message }}
                  </span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12">
              <span class="fieldsText">{{
                $t('message.pages.profile.phone')
              }}</span>
            </v-col>
            <v-col cols="12">
              <VuePhoneNumberInput
                class="vuePhone"
                default-country-code="BR"
                no-example
                :preferred-countries="['BR', 'US']"
                @update="handleUpdatePhone"
                v-model="user.phone"
                :valid-color="'#ccc'"
              />
            </v-col>
            <v-col cols="12" class="mb-5 mt-7">
              <span class="fieldsText">{{
                $t('message.pages.profile.billingAddress')
              }}</span>
            </v-col>
            <v-col cols="12">
              <span class="fieldsText">{{
                $t('message.pages.profile.country')
              }}</span>
            </v-col>
            <v-col cols="6" class="pr-2">
              <v-select-legacy
                v-model="user.address.country"
                :items="countries"
                item-value="iso"
                item-text="nicename"
                outlined
                dense
              ></v-select-legacy>
            </v-col>
            <v-col cols="12">
              <span class="fieldsText">{{
                $t('message.pages.profile.postalCode')
              }}</span>
            </v-col>
            <v-col v-if="isBrazil" cols="6" class="pr-2">
              <v-text-field
                v-model="user.address.postal_code"
                v-mask="'#####-###'"
                placeholder="Ex: 12345000"
                outlined
                dense
                maxlength="9"
                :disabled="validatingCEP"
                hide-details
              ></v-text-field>
            </v-col>
            <v-col v-else cols="6" class="pr-2">
              <v-text-field
                v-model="user.address.postal_code"
                outlined
                dense
                :disabled="validatingCEP"
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="2" class="pl-1">
              <v-row
                no-gutters
                align="center"
                justify="start"
                style="height: 40px"
              >
                <v-progress-circular
                  v-if="validatingCEP"
                  :size="20"
                  width="3"
                  color="primary"
                  indeterminate
                />
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-card flat height="30">
                <span class="red--text footerText align-text d-block my-1">
                  <span v-if="errors.unknownCepError">
                    {{ $t('message.errors.unexpectedError') }}
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon size="18" v-bind="attrs" v-on="on">
                          mdi-help-circle
                        </v-icon>
                      </template>
                      <v-container
                        class="text-body-2 pa-0"
                        style="max-width: 230px"
                      >
                        {{ errors.errMsgCep }}
                      </v-container>
                    </v-tooltip>
                  </span>
                </span>
              </v-card>
            </v-col>
            <v-col cols="12">
              <span class="fieldsText">{{
                $t('message.pages.profile.address')
              }}</span>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="user.address.line1"
                outlined
                dense
                :disabled="validatingCEP"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <span class="fieldsText pr-2">{{
                $t('message.pages.profile.city')
              }}</span>
            </v-col>
            <v-col cols="6">
              <span class="fieldsText pl-2">{{
                $t('message.pages.profile.state')
              }}</span>
            </v-col>
            <v-col cols="6" class="pr-2">
              <v-text-field
                v-model="user.address.city"
                outlined
                dense
                :disabled="validatingCEP"
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="6" class="pl-2">
              <v-text-field
                v-model="user.address.state"
                outlined
                dense
                :disabled="validatingCEP"
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" class="mt-2">
              <v-card flat height="30">
                <span class="red--text footerText align-text d-block my-1">
                  <span v-if="errors.unknownError">
                    {{ $t('message.errors.unexpectedError') }}
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon size="18" v-bind="attrs" v-on="on">
                          mdi-help-circle
                        </v-icon>
                      </template>
                      <v-container
                        class="text-body-2 pa-0"
                        style="max-width: 230px"
                      >
                        {{ errors.errMsg }}
                      </v-container>
                    </v-tooltip>
                  </span>
                </span>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-8 py-4">
          <v-row no-gutters justify="start" align="center">
            <v-btn
              color="primary"
              :disabled="!checkFormValid"
              :loading="isFetchingSave"
              @click="updateUser"
            >
              {{ $t('message.actions.save') }}
            </v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { IS_CPF, IS_CNPJ } from '@/config/envs.js'
import addressApi from '@/services/addressApi'
import planApi from '@/services/planApi'
import { mapState } from 'vuex'
import VuePhoneNumberInput from 'vue-phone-number-input'
import Storage from '@/utils/Storage'

export default {
  name: 'CustomerBilling',
  components: { VuePhoneNumberInput },
  data: () => ({
    open: false,
    isFetchingSave: false,
    user: {
      name: '',
      document: '',
      phone: '',
      address: {
        city: '',
        country: 'BR',
        line1: '',
        postal_code: '',
        state: '',
      },
    },
    errors: {
      unknownError: false,
      unknownCepError: false,
      invalidCpf: false,
      invalidCnpj: false,
      addressError: false,
      subscriptionError: false,
      userDataError: false,
      cardInfoError: false,
      errMsg: '',
      errMsgCep: '',
    },
    validatingCEP: false,
    states: [
      { id: 12, UF: 'AC' },
      { id: 27, UF: 'AL' },
      { id: 16, UF: 'AP' },
      { id: 13, UF: 'AM' },
      { id: 29, UF: 'BA' },
      { id: 23, UF: 'CE' },
      { id: 53, UF: 'DF' },
      { id: 32, UF: 'ES' },
      { id: 52, UF: 'GO' },
      { id: 21, UF: 'MA' },
      { id: 51, UF: 'MT' },
      { id: 50, UF: 'MS' },
      { id: 31, UF: 'MG' },
      { id: 15, UF: 'PA' },
      { id: 25, UF: 'PB' },
      { id: 41, UF: 'PR' },
      { id: 26, UF: 'PE' },
      { id: 22, UF: 'PI' },
      { id: 33, UF: 'RJ' },
      { id: 24, UF: 'RN' },
      { id: 43, UF: 'RS' },
      { id: 11, UF: 'RO' },
      { id: 14, UF: 'RR' },
      { id: 42, UF: 'SC' },
      { id: 35, UF: 'SP' },
      { id: 28, UF: 'SE' },
      { id: 17, UF: 'TO' },
    ],
    countries: [],
    userStartVal: null,
    firstRequest: true,
  }),
  computed: {
    ...mapState('user', ['userPlanInfo']),
    isBrazil() {
      return this.user.address.country === 'BR'
    },
    documentErrorMessage() {
      if (this.errors.invalidCpf) {
        return 'CPF inválido'
      }
      if (this.errors.invalidCnpj) {
        return 'CNPJ inválido'
      }
      return ''
    },
    computedCep() {
      return this.user.address.postal_code
    },
    checkFormValid() {
      return (
        !!this.user.name &&
        (!!this.user.document || !this.isBrazil) &&
        !!this.user.address.postal_code &&
        !!this.user.address.line1 &&
        !!this.user.address.city
      )
    },
    documentMask() {
      const doc = this.user.document
      if (doc.length > 14) {
        return ['##.###.###/####-##']
      } else {
        return ['###.###.###-##']
      }
    },
  },
  watch: {
    computedCep(oldVal, newVal) {
      if (this.unmask(oldVal) === this.unmask(newVal)) return
      const cepNumber = this.unmask(this.user.address.postal_code)
      if (
        cepNumber.length === 8 &&
        this.user.address.country === 'BR' &&
        !this.firstRequest
      ) {
        this.searchCep()
      }
      this.firstRequest = false
    },
  },
  mounted() {
    this.$root.$on('customerBillingModal', () => {
      this.firstRequest = true
      this.open = true
      this.user.name = this.userPlanInfo?.user.name
        ? JSON.parse(JSON.stringify(this.userPlanInfo?.user.name))
        : ''
      this.user.phone = this.userPlanInfo?.user.phone
        ? JSON.parse(JSON.stringify(this.userPlanInfo?.user.phone))
        : ''
      this.user.document = this.userPlanInfo?.taxId?.value
        ? JSON.parse(JSON.stringify(this.userPlanInfo?.taxId?.value))
        : ''
      if (this.userPlanInfo.address)
        this.user.address = JSON.parse(
          JSON.stringify(this.userPlanInfo?.address)
        )
      this.countries = Storage.getItem('countries')
      this.userStartVal = JSON.parse(JSON.stringify(this.user))
    })
  },
  beforeDestroy() {
    this.$root.$off('customerBillingModal')
  },
  methods: {
    async updateUser() {
      this.isFetchingSave = true
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
      let taxObject = {}
      if (this.isBrazil) {
        const doc = this.unmask(this.user?.document)
        if (!doc || (doc?.length !== IS_CNPJ && doc?.length !== IS_CPF)) {
          this.errors.invalidCpf = true
          this.isFetchingSave = false
          return
        }
        if (doc?.length === IS_CPF) {
          const isValid = this.isValidCPF(doc)
          if (!isValid) {
            this.errors.invalidCpf = true
            this.isFetchingSave = false
            return
          }
        }
        if (doc?.length === IS_CNPJ) {
          const isValidCNPJ = this.isValidCNPJ(doc)
          if (!isValidCNPJ) {
            this.errors.invalidCnpj = true
            this.isFetchingSave = false
            return
          }
        }
        taxObject = {
          value: this.user.document,
        }
        if (this.isBrazil) {
          if (doc?.length === IS_CPF) {
            taxObject['type'] = 'br_cpf'
          } else {
            taxObject['type'] = 'br_cnpj'
          }
        } else {
          if (this.userPlanInfo?.taxId?.type)
            taxObject['type'] = this.userPlanInfo.taxId.type
          if (this.userPlanInfo?.taxId?.value)
            taxObject['type'] = this.userPlanInfo.taxId.value
        }
      }
      try {
        let userData = {
          ...this.user,
        }
        if (this.isBrazil) userData['tax_obj'] = taxObject
        delete userData.document
        userData.address.postal_code = this.unmask(
          this.user.address.postal_code
        )
        await planApi.updateAddress(userData)
        this.$root.$emit('refreshUserPlan')
      } catch (e) {
        this.errors.errMsg = e.response.data.errMsg
        this.errors.unknownError = true
        this.isFetchingSave = false
      }
      this.isFetchingSave = false
      this.handleClose()
    },
    handleUpdatePhone(val) {
      this.userPhone = val.formattedNumber
    },
    unmask(text) {
      const defaultDelimiters = /[-!$%^&*()_+|~=`{}[\]:";'<>?,./\\ ]/
      return text
        ? String(text).replace(new RegExp(defaultDelimiters, 'g'), '')
        : text
    },
    async searchCep() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
      this.validatingCEP = true
      const cepNumber = this.unmask(this.user.address.postal_code)
      if (cepNumber.length === 8) {
        try {
          const ret = await addressApi.getCep(cepNumber)
          this.user.address.line1 = ret.data.address
          this.user.address.city = ret.data.name
          const stateFound = this.states.filter((state) => {
            return state.id === ret.data.state_id
          })
          this.user.address.state = stateFound[0].UF
        } catch (e) {
          this.errors.errMsgCep = e.response.data.errMsg
          this.errors.unknownCepError = true
          this.validatingCEP = false
          console.error(e)
        }
      }
      this.validatingCEP = false
    },
    isValidCPF(cpf) {
      if (typeof cpf !== 'string') return false
      cpf = cpf.replace(/[^\d]+/g, '')
      if (cpf.length !== IS_CPF || !!cpf.match(/(\d)\1{10}/)) return false
      cpf = cpf.split('')
      const validator = cpf
        .filter((digit, index, array) => index >= array.length - 2 && digit)
        .map((el) => +el)
      const toValidate = (pop) =>
        cpf
          .filter((digit, index, array) => index < array.length - pop && digit)
          .map((el) => +el)
      const rest = (count, pop) =>
        ((toValidate(pop).reduce((soma, el, i) => soma + el * (count - i), 0) *
          10) %
          11) %
        10
      return !(rest(10, 2) !== validator[0] || rest(11, 1) !== validator[1])
    },
    isValidCNPJ(cnpj) {
      if (
        !cnpj ||
        cnpj.length != 14 ||
        cnpj == '00000000000000' ||
        cnpj == '11111111111111' ||
        cnpj == '22222222222222' ||
        cnpj == '33333333333333' ||
        cnpj == '44444444444444' ||
        cnpj == '55555555555555' ||
        cnpj == '66666666666666' ||
        cnpj == '77777777777777' ||
        cnpj == '88888888888888' ||
        cnpj == '99999999999999'
      )
        return false
      let length = cnpj.length - 2
      let numbers = cnpj.substring(0, length)
      let digits = cnpj.substring(length)
      let sum = 0
      let pos = length - 7
      for (let i = length; i >= 1; i--) {
        sum += numbers.charAt(length - i) * pos--
        if (pos < 2) pos = 9
      }
      let result = sum % IS_CPF < 2 ? 0 : 11 - (sum % IS_CPF)
      if (result != digits.charAt(0)) return false
      length = length + 1
      numbers = cnpj.substring(0, length)
      sum = 0
      pos = length - 7
      for (let i = length; i >= 1; i--) {
        sum += numbers.charAt(length - i) * pos--
        if (pos < 2) pos = 9
      }
      result = sum % IS_CPF < 2 ? 0 : IS_CPF - (sum % IS_CPF)
      if (result != digits.charAt(1)) return false
      return true
    },
    handleClose() {
      this.open = false
    },
    handleDocumentValidate() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
      const doc = this.unmask(this.user.document)
      if (doc.length === IS_CPF) {
        this.errors.invalidCpf = !this.isValidCPF(doc)
      }
      if (doc.length === IS_CNPJ) {
        this.errors.invalidCnpj = !this.isValidCNPJ(doc)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.title {
  font-weight: 500;
  font-size: 18px;
  color: var(--v-neutral-lighten1);
}
.fieldsText {
  font-weight: 500;
  font-size: 16px;
  color: var(--v-neutral-lighten1);
}

.vuePhone input {
  height: 50px !important;
  min-height: 50px !important;
}
</style>
